/**
 * NewsContent — left side of news detail page (news content body).
 *
 * Shows: source meta, headline, AI summary (or placeholder if pending/failed),
 * raw_excerpt, source link, byline ("Asklaw Editorial Team" per GNPC), tag chips.
 */

import { Link } from 'react-router-dom';
import { useLang } from '../../use-lang';
import { resolveHotNews } from '../../i18n/hotnews';
import { NewsTagChip } from './NewsTagChip';
import { NewsMicroActionsRow } from './NewsMicroActionsRow';
import type { PlayfulTag } from '../../i18n/hotnews';

export interface NewsContentData {
  id: string;
  slug: string;
  source_id: string;
  source_name: string;
  source_url: string;
  headline: string;
  ai_summary: string | null;
  ai_summary_status: 'pending' | 'generated' | 'failed' | 'rejected';
  raw_excerpt: string | null;
  topic: string | null;
  detail_mode: 'case_event' | 'new_law' | 'mixed';
  tags: PlayfulTag[];
  published_at: number;
}

export interface NewsContentProps {
  news: NewsContentData;
}

export function NewsContent({ news }: NewsContentProps) {
  const { lang } = useLang();
  const copy = resolveHotNews(lang);

  const publishedDate = new Date(news.published_at * 1000);
  const byline = lang === 'en' ? 'Asklaw Editorial Team' : 'Đội biên tập Asklaw';

  return (
    <article
      className="news-content"
      aria-labelledby={`news-detail-headline-${news.id}`}
    >
      <header style={{ marginBottom: 16 }}>
        <div style={metaRowStyle}>
          <Link
            to={`/news/source/${news.source_id}`}
            style={{ color: 'var(--asklaw-orange-deep)', fontWeight: 600, textDecoration: 'none' }}
          >
            {news.source_name}
          </Link>
          <span aria-hidden style={{ color: 'var(--asklaw-muted)' }}>·</span>
          <time
            dateTime={publishedDate.toISOString()}
            style={{ color: 'var(--asklaw-muted)', fontSize: 13 }}
          >
            {publishedDate.toLocaleDateString(lang === 'vi' ? 'vi-VN' : 'en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </time>
          <span aria-hidden style={{ color: 'var(--asklaw-muted)' }}>·</span>
          <span style={{ color: 'var(--asklaw-muted)', fontSize: 13 }}>
            {lang === 'en' ? 'By' : 'Bởi'} {byline}
          </span>
        </div>

        <h1
          id={`news-detail-headline-${news.id}`}
          className="h-display"
          style={{
            fontSize: 'clamp(24px, 4vw, 36px)',
            margin: '8px 0 0',
            lineHeight: 1.25,
          }}
        >
          {news.headline}
        </h1>
      </header>

      {news.ai_summary_status === 'generated' && news.ai_summary ? (
        <section aria-labelledby="news-summary-heading" style={summaryStyle}>
          <h2 id="news-summary-heading" style={summaryLabelStyle}>
            {copy.card_summary_label}
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 16, lineHeight: 1.65 }}>
            {news.ai_summary}
          </p>
        </section>
      ) : (
        <div role="status" className="banner banner-warning" style={{ margin: '12px 0' }}>
          {copy.error_summary_failed}
        </div>
      )}

      {news.raw_excerpt ? (
        <section aria-labelledby="news-excerpt-heading" style={{ marginTop: 16 }}>
          <h2 id="news-excerpt-heading" style={smallHeadingStyle}>
            {lang === 'en' ? 'Source excerpt' : 'Trích đoạn từ nguồn'}
          </h2>
          <p style={{ fontSize: 15, lineHeight: 1.65, color: 'var(--asklaw-ink-soft)' }}>
            {news.raw_excerpt}
          </p>
        </section>
      ) : null}

      <p style={{ marginTop: 16, fontSize: 14 }}>
        <a
          href={news.source_url}
          target="_blank"
          rel="noopener noreferrer nofollow"
          style={{ color: 'var(--asklaw-blue)', fontWeight: 600 }}
        >
          {lang === 'en' ? 'Read original →' : 'Đọc bản gốc →'}
        </a>
      </p>

      {news.tags.length > 0 ? (
        <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {news.tags.map((tag) => (
            <NewsTagChip key={tag} tag={tag} />
          ))}
        </div>
      ) : null}

      <div style={{ marginTop: 24 }}>
        <NewsMicroActionsRow
          newsId={news.id}
          slug={news.slug}
          headline={news.headline}
        />
      </div>
    </article>
  );
}

const metaRowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 8,
  fontSize: 13,
  fontFamily: 'var(--font-sans)',
};

const summaryStyle: React.CSSProperties = {
  marginTop: 16,
  padding: '16px 18px',
  background: 'var(--asklaw-cream-deep)',
  borderLeft: '4px solid var(--asklaw-orange)',
  borderRadius: '0 10px 10px 0',
};

const summaryLabelStyle: React.CSSProperties = {
  display: 'inline-block',
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  color: 'var(--asklaw-orange-deep)',
  fontWeight: 600,
  margin: 0,
};

const smallHeadingStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  color: 'var(--asklaw-muted)',
  fontWeight: 600,
  marginBottom: 6,
};
